import type { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatUsd } from "@/admin/shared/utils";
import type { PaymentShowData } from "@/admin/entities/payments";
import type {
  FeeTypeType,
  ProviderFeePayerType,
} from "@/types/graphql/admin.generated";

type PaymentAmount = PaymentShowData["amount"] | null | undefined;
type PaymentFeeSettings = PaymentShowData["feeSettings"];

// Те же подписи, что в деталях организации: комиссия платежа — снапшот
// её настроек на момент оплаты.
const PROVIDER_FEE_PAYER_LABEL: Record<ProviderFeePayerType, string> = {
  ORGANIZATION: "организация",
  OTTKIT: "платформа",
  IN_HALF: "пополам",
};

function formatFeeSettings(feeSettings: NonNullable<PaymentFeeSettings>) {
  const { feeType, percentageFee, fixedFeeAmount } = feeSettings;
  const fixedPart = `${fixedFeeAmount.value} ${fixedFeeAmount.currency}`;

  const byType: Record<FeeTypeType, string> = {
    PERCENTAGE: `${percentageFee} %`,
    FIXED: `фиксированная ${fixedPart}`,
    FIXED_PERCENTAGE_SUM: `${fixedPart} + ${percentageFee} %`,
  };

  return byType[feeType];
}

interface PaymentAmountsProps {
  payment: PaymentShowData;
}

const formatAmount = (amount: PaymentAmount): string =>
  amount ? `${amount.value} ${amount.currency}` : "";

/**
 * Раскладка платежа на суммы и комиссии. Строки показываются всегда, даже
 * пустые: у платежей без подтверждённой комиссии провайдера часть сумм
 * приходит null, и пустая строка честнее отсутствующей.
 */
export const PaymentAmounts: FC<PaymentAmountsProps> = ({ payment }) => {
  const rows: { label: string; amount: PaymentAmount }[] = [
    { label: "Сумма платежа", amount: payment.amount },
    {
      label: "Сумма в валюте организации",
      amount: payment.amountInOrganizationCurrency,
    },
    { label: "Комиссия провайдера", amount: payment.providerFeeAmount },
    { label: "Комиссия платформы", amount: payment.ottkitFeeAmount },
    { label: "Комиссия всего", amount: payment.feeAmount },
    { label: "Доход организации", amount: payment.organizationAmount },
    {
      label: "Доход организации в валюте организации",
      amount: payment.organizationAmountInOrganizationCurrency,
    },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Суммы
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Показатель</TableCell>
              <TableCell align="right">Сумма</TableCell>
              <TableCell align="right">USD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ label, amount }) => (
              <TableRow key={label}>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {label}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatAmount(amount)}</TableCell>
                <TableCell align="right">
                  {formatUsd(amount?.valueUSD)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {payment.feeSettings && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mt: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Комиссия платформы по правилам платежа
              </Typography>
              <Typography variant="body1">
                {formatFeeSettings(payment.feeSettings)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Комиссию провайдера платит
              </Typography>
              <Typography variant="body1">
                {PROVIDER_FEE_PAYER_LABEL[payment.feeSettings.providerFeePayer]}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
